import { createContext, useContext } from 'react';
import type { FeatureGrant, Role } from '@visionqc/shared';

export interface AuthUser {
  id: number;
  name: string;
  role: Role;
  /** The tenant a CUSTOMER belongs to; null for staff. */
  customerId?: number | null;
  /**
   * What this tenant may use, at what level. `null`/undefined = unrestricted
   * (staff see everything); an array (possibly empty) for a customer. Phase 2's
   * tenant nav reads this to show exactly what the customer is entitled to.
   */
  features?: FeatureGrant[] | null;
}

export interface AuthState {
  user: AuthUser | null;
  authRequired: boolean;
  loading: boolean;
  /** Where tenants sign in; empty on a single-host deployment. */
  portalUrl: string;
  /** Where staff sign in; used to send staff off the portal host. */
  adminUrl: string;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthState>({
  user: null,
  authRequired: true,
  loading: true,
  portalUrl: '',
  adminUrl: '',
  refresh: async () => undefined,
  logout: async () => undefined,
});

export const useAuth = () => useContext(AuthContext);
/** True when the signed-in user may perform admin-only actions. */
export const useIsAdmin = () => useAuth().user?.role === 'ADMIN';

async function post<T>(path: string, body?: unknown): Promise<T> {
  // Only announce a JSON body when there IS one. A bodyless POST (logout) that
  // still sends content-type: application/json is rejected by Fastify with
  // "Body cannot be empty" (400) — which is exactly why sign-out silently failed
  // while change-password (which has a body) worked.
  const res = await fetch(path, {
    method: 'POST',
    ...(body === undefined
      ? {}
      : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(err.message || 'Request failed');
  }
  return res.json() as Promise<T>;
}

export const authApi = {
  me: async (): Promise<{
    user: AuthUser | null;
    authRequired: boolean;
    environment: string;
    environmentLabel: string;
    portalUrl: string;
    adminUrl: string;
    demo: boolean;
  }> => {
    const res = await fetch('/api/auth/me');
    // Unreachable backend falls back to "production": the marking is a safety
    // cue, and the failure that costs something is a staging screen that looks
    // like production, never the other way round.
    if (!res.ok) {
      return { user: null, authRequired: true, environment: 'production', environmentLabel: '', portalUrl: '', adminUrl: '', demo: false };
    }
    const body = await res.json();
    return { environment: 'production', environmentLabel: '', portalUrl: '', adminUrl: '', demo: false, ...body };
  },
  /** Login options: which credentials this deployment accepts, plus the picker. */
  users: async (): Promise<{
    accounts: AuthUser[];
    pickerEnabled: boolean;
    pinEnabled: boolean;
  }> => {
    const res = await fetch('/api/auth/users');
    if (!res.ok) return { accounts: [], pickerEnabled: false, pinEnabled: false };
    return res.json();
  },
  login: (name: string, credential: { password?: string; pin?: string }) =>
    post<{ user: AuthUser; expiresAt: string; warning?: string }>('/api/auth/login', {
      name,
      ...credential,
    }),
  /** Start a session from an on-site device key (kiosk auto-login). */
  device: (key: string) =>
    post<{ user: AuthUser; expiresAt: string; warning?: string }>('/api/auth/device', { key }),
  changePassword: (newPassword: string, currentPassword?: string) =>
    post<{ ok: boolean }>('/api/auth/change-password', { newPassword, currentPassword }),
  logout: () => post<{ ok: boolean }>('/api/auth/logout'),
};
